import React from 'react';
import PropTypes from 'prop-types';
import { GetBusinesses, GetUserDetails } from '../../api/home';
import { useAuth } from '../../contexts/auth';
import {
  handlAuthAllBusinesses,
  handlAuthUserType,
  handlAuthSid,
  handlAuthUserDetails,
  handleProfileDetails,
} from '../../reducersAndActions/actions/authAction';
import {
  clickTracker,
  getCookie,
  getProjectId,
  getSourceValue,
  setCookie,
  setSidInCookies,
  touchSource,
  webSource,
} from '../../lib/common';
import useSelectedBusiness from '../../hooks/useSelectedBusiness';
import { useSessionStorage } from '../../hooks';
import { logger, onPageEvent } from '../../lib/gaAnalytics';
import { getCsPortalLog } from '../../lib/logger';
import { decodeToken, getContractType } from '../../api';
import { server } from '../../config';
import { handleLoader, handleLoginPopup } from '../../reducersAndActions/actions/appStateActions';
import { useAppState } from '../../contexts/appState';

export default function Auth({ children }) {
  document.getElementById('landingHeader').style.display = 'none';
  const [authState, dispatch] = useAuth();
  const [appState, appDispatch] = useAppState();
  const { checkAndSetSelectedBusiness } = useSelectedBusiness();
  const [, setUserSession] = useSessionStorage('userDetails', '');
  const [profileDetailsSession, setProfileDetailsSession] = useSessionStorage('profileDetails', '');
  const queryParams = new URLSearchParams(window.location.search);
  const docIdParam = queryParams.get('docid') || '';
  const source = getSourceValue();
  const { genioRedirectionUrl } = server;
  const landingPageUserType = sessionStorage.getItem('landingPageUserType');
  const projectId = getProjectId();

  //   Android && IOS callback function
  window.checkSid = (logjson) => {
    const dataJSON = JSON.parse(logjson);
    const { sid, mobile } = dataJSON;

    if (!sid || sid === '') {
      handleLoginPopup(appDispatch, { ...appState.loginPopup, open: true, login: true, nonlogin: true });
    }
    setSidInCookies(sid);
    handlAuthSid(dispatch, sid);
    setCookie('mobile', mobile);
  };

  React.useEffect(() => {
    const handleUserDetails = async () => {
      onPageEvent(window.location.pathname, 'CS-Portal');
      try {
        if (window.location.pathname.includes('csportal')) {
          handleLoader(appDispatch, { ...appState, loader: true });
          const vParams = queryParams.get('vertical') || null;
          if (vParams) {
            await clickTracker(`CS-Landing-leadDashBoard`, 'leadDashBoard');
          }
          if (
            (!getCookie('sid') && touchSource.includes(source)) ||
            (!getCookie('sid') && webSource.includes(source))
          ) {
            handleLoginPopup(appDispatch, { ...appState.loginPopup, open: true, login: true });
          } else if (!getCookie('sid') && ['1', 1].includes(source)) {
            // for android
            const logjson = JSON.stringify({ fn: 'checkSid', fnc: 'checkSid' });
            window.JdLiteInterface.verticalLogin(logjson);
          } else if (!getCookie('sid') && ['3', 3].includes(source)) {
            const logjson = JSON.stringify({ type: 'verticalLogin', fn: 'checkSid', fnc: 'checkSid' });
            window.webkit.messageHandlers.callbackHandler.postMessage(logjson);
          }

          let sid = getCookie('sid') || '';
          if (sid) handlAuthSid(dispatch, sid);

          const { fetch } = window;
          window.fetch = (...args) =>
            // eslint-disable-next-line no-shadow
            (async (args) => {
              let result = null;
              try {
                result = await fetch(...args);
              } catch (e) {
                console.log('Unauthorised access, logging out', e);
              }
              if (!result || result.status === 401) {
                sid = getCookie('sid') || '';
                await logger(
                  'CSPORTAL',
                  getCsPortalLog('Routes inside fetch', source, '', {
                    url: window.location.href,
                    sid,
                    result,
                    source,
                  }),
                  true,
                );
                handleLoginPopup(appDispatch, {
                  ...appState.loginPopup,
                  open: !sid || sid === '',
                  nonlogin: !sid || sid === '',
                });
              }
              return result;
            })(args);
        } else if (window.location.pathname.includes('genio')) {
          const token = queryParams.get('token');
          window.sidToken = token;

          if ((!authState?.profileDetails?.empCode && !profileDetailsSession?.empCode) || !getCookie('EmpCode')) {
            try {
              if (window.sidToken) {
                const res = await decodeToken(token);
                if (res?.errorCode === 0) {
                  handleProfileDetails(dispatch, res?.result || {});
                  setProfileDetailsSession(res?.result || {});
                  setCookie('Authorization', window.sidToken);
                  setCookie('EmpCode', res?.result?.empCode || '');
                  if (res?.result?.companyDetails) {
                    localStorage.removeItem('selectedBusinessGenio');
                    localStorage.setItem('selectedBusinessGenio', JSON.stringify(res?.result?.companyDetails || {}));
                  }
                } else if (res?.errorCode === 1 || res?.message === 'redirect') {
                  window.location = genioRedirectionUrl || '';
                }
              } else {
                window.location = genioRedirectionUrl || '';
              }
            } catch (error) {
              console.log('Error in decode Token', error);
            }
          }
        }
        handleLoader(appDispatch, { ...appState, loader: false });
      } catch (err) {
        console.log('Error: ', err);
        handleLoader(appDispatch, { ...appState, loader: false });
      }
    };
    handleUserDetails();
  }, []);

  React.useEffect(() => {
    const handleCsportalUser = async () => {
      try {
        const sid = getCookie('sid') || '';
        if (sid) {
          handlAuthSid(dispatch, sid);
        }
        handleLoader(appDispatch, { ...appState, loader: true });
        if (authState?.sidResponse || sid) {
          handleLoginPopup(appDispatch, {
            ...appState.loginPopup,
            open: false,
            nonlogin: false,
          });
          const businessDataObj = JSON.parse(localStorage.getItem('selectedBusiness')) || {};
          const [user, business] = await Promise.all([GetUserDetails(), GetBusinesses(docIdParam)]);
          let contractTypeRes = {};
          if (projectId === 'csportal' && businessDataObj?.parentid) {
            contractTypeRes = await getContractType(businessDataObj?.parentid, businessDataObj?.data_city);
            sessionStorage.setItem('contractType', JSON.stringify(contractTypeRes?.result || ''));
          }
          if (!business || business.errorCode !== 0 || !business.result || !business.result.length) {
            handlAuthUserType(dispatch, 'user');
          } else {
            handlAuthUserType(dispatch, 'business');
            const data =
              (business && business.result && business.result.sort((a) => (a.paidstatus === 1 ? -1 : 1))) || [];
            handlAuthAllBusinesses(dispatch, data);
            checkAndSetSelectedBusiness();
          }
          // Set all user details
          const resData = user?.result;
          if (resData) {
            const userDetails = {
              fullName: resData.full_name || '',
              mobile: resData.mobile || '',
              cityName: resData.city_name || '',
              email: resData?.email_info?.[0] ? resData?.email_info?.[0]?.email : '',
              jduid: resData.jduid || '',
            };
            handlAuthUserDetails(dispatch, userDetails);
            setUserSession(userDetails);
          }
        } else {
          handlAuthUserType(dispatch, landingPageUserType);
          handleLoginPopup(appDispatch, {
            ...appState.loginPopup,
            open: true,
            nonlogin: true,
          });
        }
        handleLoader(appDispatch, { ...appState, loader: false });
      } catch (e) {
        console.log('Error in handleCsportalUser', e);
        handleLoader(appDispatch, { ...appState, loader: false });
        //   resetCsportalUser();
      }
      handleLoader(appDispatch, { ...appState, loader: false });
    };
    handleCsportalUser();
  }, [authState?.sidResponse]);

  return <>{children}</>;
}

Auth.propTypes = {
  children: PropTypes.any,
};
